// Yin Yoga Wells - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Moon, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const YinYogaWells = () => {
  usePageMeta({
    title: "Yin Yoga Wells | Slow Yoga | Annelie Lily",
    description: "Yin yoga classes for Wells residents. Slow, deep stretches held for 3-5 minutes to release tension. Based in Frome, 25 minutes from Wells through the",
    canonical: "https://annelielily.com/areas/yoga-wells/yin-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Yin Yoga Classes for Wells Residents",
    description: "Yin yoga classes for Wells residents at Annelie Lily Yoga in Frome. Slow, meditative practice targeting deep connective tissue.",
    serviceType: "Yoga Classes",
    url: "https://annelielily.com/areas/yoga-wells/yin-yoga"
  });

  const faqs = [
    { question: "What is Yin yoga?", answer: "Yin yoga is a slow, meditative practice where poses are held for 3-5 minutes. It targets deep connective tissue — fascia, ligaments, joints — releasing tension that faster yoga styles don't reach." },
    { question: "How far is Frome from Wells?", answer: "About 25 minutes through the Mendips via Shepton Mallet. It's a scenic drive through Somerset countryside." },
    { question: "Can I do Yin yoga if I'm very tight?", answer: "Absolutely. Yin is ideal for tight bodies. We use props to make every pose comfortable, and the long holds give your body time to release gradually." },
    { question: "Is Yin yoga suitable for beginners?", answer: "Yes. There's no complicated sequencing — just simple poses held long enough to feel the benefit." },
    { question: "How is Yin different from Hatha yoga?", answer: "Hatha is more active — you move between poses. Yin is passive — you settle into poses and stay. Many students do both; they complement each other." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yin Yoga" currentSlug="areas/yoga-wells/yin-yoga" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yin Yoga for Wells</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yin Yoga Wells</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">Deep, slow yoga for Wells residents. Long-held poses that release tension — 25 minutes through the Mendips.</p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />25 min from Wells</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Yin Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is Yin yoga?</h2>
          <p className="text-neutral-700">Yin yoga is a slow practice where poses are held for 3-5 minutes, targeting deep connective tissue. It's deeply relaxing and ideal for releasing tension, improving flexibility, and calming the nervous system.</p>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who is Yin yoga for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[{ title: "Desk workers", desc: "Release tension from sitting — hips, lower back, shoulders" }, { title: "Stressed or anxious", desc: "The slow pace calms the nervous system" }, { title: "Athletes", desc: "Release deep tension that stretching doesn't reach" }, { title: "Anyone feeling tight", desc: "Yin gives your body time to release gradually" }].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10"><h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3><p className="text-neutral-600 text-sm">{item.desc}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a Yin class</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">1</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Settling in</h3><p className="text-sm">We begin gently, allowing you to arrive and let go of the day.</p></div></div>
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">2</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Long-held poses</h3><p className="text-sm">Each pose is held for 3-5 minutes with props for comfort.</p></div></div>
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">3</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Gradual release</h3><p className="text-sm">Your body gradually releases — deeper than active stretching.</p></div></div>
            <div className="flex gap-4"><div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1"><span className="text-gold font-semibold text-sm">4</span></div><div><h3 className="font-heading text-lg text-neutral-800 mb-1">Deep relaxation</h3><p className="text-sm">Extended relaxation at the end. You'll leave feeling calmer.</p></div></div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Wells residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p><Link to="/areas/yoga-wells" className="text-gold hover:underline">Wells</Link> is about 25 minutes from Frome through the Mendips. The scenic drive through Somerset helps you leave the day behind, arriving ready to unwind.</p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of Yin yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[{ icon: Moon, text: "Deep tension release" }, { icon: Heart, text: "Calms nervous system" }, { icon: Clock, text: "Improves flexibility" }, { icon: Check, text: "Better sleep" }].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3"><item.icon className="w-5 h-5 text-gold flex-shrink-0" /><span className="text-neutral-700">{item.text}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions about Yin yoga in Wells</h2>
          <div className="space-y-4">{faqs.map((faq, index) => (<div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10"><h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3><p className="text-neutral-600 text-sm">{faq.answer}</p></div>))}</div>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">I'm Annelie, offering small Yin classes with individual attention. <Link to="/about" className="text-gold hover:underline">Learn more</Link> or <Link to="/about" className="text-gold hover:underline">read what students say</Link>.</p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to try Yin yoga?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Wells residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-wells/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga Wells</Link><span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-wells/private-yoga" className="text-sm text-gold hover:underline">Private Sessions</Link><span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-wells" className="text-sm text-gold hover:underline">All Classes</Link><span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga (Main)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YinYogaWells;
